import { XMarkIcon } from '@heroicons/react/24/outline';
import { AnimatePresence, motion } from 'framer-motion';

import { DrawerResizeHandle } from '@/components/legacy-ui/DrawerResizeHandle';
import { usePanelResize } from '@/hooks/usePanelResize';
import { useAutoDrawerStore } from '@/sections/insights/aiAssistant/stores/autoDrawer.store';
import { HuntStatusChip } from '@/sections/insights/hunts/HuntStatusChip';
import { useHunts } from '@/sections/insights/hunts/useHunts';
import type { Hunt } from '@/types';

import { AutoStatusView } from './AutoStatusView';

const MIN_WIDTH = 280;
const MAX_WIDTH = 760;
const STORAGE_KEY = 'ai-assistant-auto-drawer-width';

/**
 * HuntStatusDrawer - Right-side drawer showing hunt status (AutoStatusView).
 * Follows the same visual pattern as TraceDrawer: framer-motion slide
 * animation, resizable width persisted to localStorage.
 */
export function HuntStatusDrawer() {
  const isOpen = useAutoDrawerStore(s => s.isOpen);
  const huntUuid = useAutoDrawerStore(s => s.huntUuid);
  const close = useAutoDrawerStore(s => s.close);

  const { data: hunts = [] } = useHunts();

  const activeHunt: Hunt | undefined = huntUuid
    ? (hunts as Hunt[]).find((h: Hunt) => h.uuid === huntUuid)
    : undefined;

  const { width: drawerWidth, onResizeHandleMouseDown } = usePanelResize({
    storageKey: STORAGE_KEY,
    minWidth: MIN_WIDTH,
    maxWidth: MAX_WIDTH,
    defaultWidth: MAX_WIDTH, // open at max by default; a stored width still wins
  });

  const showStatusView = !!activeHunt;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ x: '100%', opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: '100%', opacity: 0 }}
          transition={{
            type: 'tween',
            duration: 0.25,
            ease: 'easeOut',
          }}
          className="bg-chariot-background-primary border-chariot-stroke absolute top-0 right-0 z-20 flex h-full flex-col border-l shadow-lg"
          style={{ width: `${drawerWidth}px` }}
        >
          {/* Resize handle */}
          <DrawerResizeHandle
            onMouseDown={onResizeHandleMouseDown}
            className="absolute top-1/2 -left-10 z-30 -translate-y-1/2"
          />

          {/* Header: status chip + UUID + close */}
          <div className="border-chariot-stroke border-b">
            <div className="flex items-center justify-between gap-3 px-4 py-2">
              {/* Left cluster: chip (fixed width) + UUID (flexible, truncates) */}
              <div className="flex min-w-0 items-center gap-3">
                {activeHunt && (
                  <div className="shrink-0">
                    <HuntStatusChip status={activeHunt.status} />
                  </div>
                )}
                <span
                  className="text-chariot-text-secondary truncate font-mono text-xs"
                  title={activeHunt?.uuid}
                >
                  {activeHunt?.uuid}
                </span>
              </div>
              <button
                type="button"
                onClick={close}
                className="text-chariot-text-secondary hover:text-chariot-text-primary shrink-0 rounded p-1 transition-colors"
                aria-label="Close drawer"
              >
                <XMarkIcon className="size-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {showStatusView && <AutoStatusView hunt={activeHunt} />}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// Legacy alias — kept for test compatibility
export { HuntStatusDrawer as AutoDrawer };

export default HuntStatusDrawer;
